import { SOLANA_CHAIN_ID } from "@repo/caip";

export type VmId = "evm" | "solana" | "sui" | `other:${string}`;

export type VmPreference = "auto" | VmId;

export type PlatformChainPreference = "evm" | "solana" | "sui";

export type PortfolioRuntimeVm = Extract<VmId, "evm" | "solana">;
export type PortfolioNetwork = "EVM" | "SOLANA";

const SOLANA_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
const SUI_ADDRESS_PATTERN = /^0x[0-9a-f]{64}$/;

const PLATFORM_CHAIN_PREFERENCE_BY_VM: Readonly<
  Partial<Record<VmId, PlatformChainPreference>>
> = {
  evm: "evm",
  solana: "solana",
  sui: "sui",
};

const PORTFOLIO_NETWORK_BY_VM: Readonly<
  Partial<Record<VmId, PortfolioNetwork>>
> = {
  evm: "EVM",
  solana: "SOLANA",
};

const DEFAULT_PORTFOLIO_CHAIN_ID_BY_VM: Readonly<
  Partial<Record<VmId, number>>
> = {
  evm: 8453,
  solana: SOLANA_CHAIN_ID,
};

export function parseVmId(value: unknown): VmId | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === "evm" || normalized === "solana" || normalized === "sui") {
    return normalized;
  }

  if (normalized.startsWith("other:") && normalized.length > "other:".length) {
    return normalized as `other:${string}`;
  }

  return null;
}

export function toPortfolioRuntimeVm(
  vmId: VmId | null | undefined,
): PortfolioRuntimeVm | null {
  return vmId === "evm" || vmId === "solana" ? vmId : null;
}

export function toPortfolioNetwork(
  vmId: VmId | null | undefined,
): PortfolioNetwork | null {
  return vmId ? (PORTFOLIO_NETWORK_BY_VM[vmId] ?? null) : null;
}

export function toDefaultPortfolioChainId(
  vmId: VmId | null | undefined,
): number | null {
  return vmId ? (DEFAULT_PORTFOLIO_CHAIN_ID_BY_VM[vmId] ?? null) : null;
}

export function resolvePortfolioChainId(
  vmId: VmId | null | undefined,
  preferredChainId?: number | null,
): number | null {
  const runtimeVm = toPortfolioRuntimeVm(vmId);
  if (!runtimeVm) {
    return null;
  }

  return preferredChainId ?? toDefaultPortfolioChainId(runtimeVm);
}

export function normalizeVmId(
  rawChainType?: string | null,
  address?: string | null,
): VmId {
  const normalizedChainType = rawChainType?.toLowerCase().trim();
  const parsedChainType = parseVmId(normalizedChainType);

  if (parsedChainType) {
    return parsedChainType;
  }

  if (normalizedChainType === "ethereum" || normalizedChainType === "evm") {
    return "evm";
  }

  if (normalizedChainType === "solana" || normalizedChainType === "svm") {
    return "solana";
  }

  if (normalizedChainType === "sui") {
    return "sui";
  }

  if (normalizedChainType) {
    return `other:${normalizedChainType}`;
  }

  const normalizedAddress = address?.trim().toLowerCase();
  if (normalizedAddress && SUI_ADDRESS_PATTERN.test(normalizedAddress)) {
    return "sui";
  }

  if (normalizedAddress?.startsWith("0x")) {
    return "evm";
  }

  if (address && SOLANA_ADDRESS_PATTERN.test(address.trim())) {
    return "solana";
  }

  return "other:unknown";
}

export function toPlatformChainPreference(
  vmPreference: VmPreference | null | undefined,
): PlatformChainPreference | undefined {
  return vmPreference && vmPreference !== "auto"
    ? PLATFORM_CHAIN_PREFERENCE_BY_VM[vmPreference]
    : undefined;
}
